import React, { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { AlertTriangle, RefreshCw, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { log } from '@/lib/logger';
import { isRecoverableChunkFailure, forceHardReload, clearChunkReloadMarkers } from '@/lib/lazyWithRetry';

// ─── Reload loop detection ──────────────────────────────────────────
// Tracks how many times the user has triggered an error-boundary reload
// within a short window. If we detect a loop, show a "stuck" screen
// instead of endlessly reloading.

const RELOAD_LOOP_KEY = 'nynch_error_reload_timestamps';
const MAX_ERROR_RELOADS = 3;
const ERROR_RELOAD_WINDOW_MS = 60_000; // 60 seconds

export function isReloadLooping(): boolean {
  try {
    const raw = localStorage.getItem(RELOAD_LOOP_KEY);
    const timestamps: number[] = raw ? JSON.parse(raw) : [];
    const now = Date.now();
    const recent = timestamps.filter(t => now - t < ERROR_RELOAD_WINDOW_MS);
    return recent.length >= MAX_ERROR_RELOADS;
  } catch {
    return false;
  }
}

export function recordErrorReload(): void {
  try {
    const raw = localStorage.getItem(RELOAD_LOOP_KEY);
    const timestamps: number[] = raw ? JSON.parse(raw) : [];
    const now = Date.now();
    const recent = timestamps.filter(t => now - t < ERROR_RELOAD_WINDOW_MS);
    recent.push(now);
    localStorage.setItem(RELOAD_LOOP_KEY, JSON.stringify(recent));
  } catch {
    // localStorage unavailable — can't track, just proceed
  }
}

/** True when an error screen's refresh ran within the reload window. */
function wasRefreshedJustNow(): boolean {
  try {
    const raw = localStorage.getItem(RELOAD_LOOP_KEY);
    const timestamps: number[] = raw ? JSON.parse(raw) : [];
    const now = Date.now();
    return timestamps.some(t => now - t < ERROR_RELOAD_WINDOW_MS);
  } catch {
    return false;
  }
}

/**
 * Whether to tell the user a new version is waiting. A refresh cures a stale
 * version, so a failure that returns straight after one is a real crash: it
 * must show its message and reach Sentry rather than ask for another refresh.
 */
export function isStaleVersionFailure(error: unknown): boolean {
  return isRecoverableChunkFailure(error) && !wasRefreshedJustNow();
}

// ─── ErrorBoundary component ────────────────────────────────────────

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  isChunkError: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    isChunkError: false };

  public static getDerivedStateFromError(error: Error): State {
    const isChunk = isStaleVersionFailure(error);
    return { hasError: true, error, isChunkError: isChunk };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const isChunk = isStaleVersionFailure(error);
    // Stale-chunk TypeErrors (NYNCH-D3 / NYNCH-78) are deploy artifacts.
    // Show the refresh UI; do not open a new Sentry issue for them.
    if (!isChunk) {
      Sentry.withScope((scope) => {
        scope.setExtra('componentStack', errorInfo.componentStack);
        scope.setExtra('isChunkLoadError', isChunk);
        Sentry.captureException(error);
      });
    }
    log.info('ErrorBoundary caught an error', {
      error: error.message,
      isChunkLoadError: isChunk });
  }

  private handleReset = (): void => {
    recordErrorReload();
    this.setState({ hasError: false, error: null, isChunkError: false });
    if (this.props.onReset) {
      this.props.onReset();
    } else {
      window.location.reload();
    }
  };

  private handleHardReload = (): void => {
    recordErrorReload();
    forceHardReload();
  };

  private handleStartFresh = (): void => {
    localStorage.removeItem(RELOAD_LOOP_KEY);
    clearChunkReloadMarkers();
    window.location.href = window.location.origin;
  };

  public render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Circuit breaker: if we've reloaded too many times, stop the loop
      if (isReloadLooping()) {
        return (
          <div className="min-h-screen bg-background flex items-center justify-center p-4">
            <div className="bg-card border border-border rounded-lg p-8 max-w-md w-full shadow-lg">
              <div className="flex items-center justify-center mb-4">
                <AlertTriangle className="h-12 w-12 text-danger" />
              </div>
              <h1 className="text-2xl font-bold text-foreground text-center mb-2">
                Having trouble loading
              </h1>
              <p className="text-muted-foreground text-center mb-6">
                Nynch is having difficulty loading on this device. This usually resolves on its own.
                Please wait a moment and try again, or switch to a desktop browser.
              </p>
              <Button
                onClick={this.handleStartFresh}
                className="w-full"
                variant="outline"
              >
                Start Fresh
              </Button>
            </div>
          </div>
        );
      }

      // Special UI for chunk load errors (app was updated)
      if (this.state.isChunkError) {
        return (
          <div className="min-h-screen bg-background flex items-center justify-center p-4">
            <div className="bg-card border border-border rounded-lg p-8 max-w-md w-full shadow-lg">
              <div className="flex items-center justify-center mb-4">
                <Sparkles className="h-12 w-12 text-ai-ink" />
              </div>
              <h1 className="text-2xl font-bold text-foreground text-center mb-2">
                App Updated
              </h1>
              <p className="text-muted-foreground text-center mb-6">
                A new version of Nynch is available. Please refresh to get the latest features and fixes.
              </p>
              <Button
                onClick={this.handleHardReload}
                className="w-full"
                variant="default"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh Now
              </Button>
            </div>
          </div>
        );
      }

      // Generic error UI
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-lg p-8 max-w-md w-full shadow-lg">
            <div className="flex items-center justify-center mb-4">
              <AlertTriangle className="h-12 w-12 text-danger" />
            </div>
            <h1 className="text-2xl font-bold text-foreground text-center mb-2">
              Something went wrong
            </h1>
            <p className="text-muted-foreground text-center mb-6">
              We're sorry, but something unexpected happened. Please try refreshing the page.
            </p>
            {this.state.error && (
              <div className="bg-muted/30 border border-border rounded p-4 mb-6">
                <p className="text-xs text-muted-foreground font-mono break-all">
                  {this.state.error.message}
                </p>
              </div>
            )}
            <Button
              onClick={this.handleReset}
              className="w-full"
              variant="default"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Reload Page
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
